import {
  useEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Input, InputCss } from './Input';
import { DateFormatStringEnum, initDate, isValidDate } from '../../../../utils/datetime';
import { useFormConfigurationContext } from '../../../..';

const Icon = styled.i` ${({ theme }) => `
  color: ${theme.textNeutralDark}; 
  position: absolute;
  top: ${theme.padding[2]};
  right: ${theme.padding[4]};
  cursor: pointer;
  pointer-events: none;
`}`;

const DatePickerInputContainer = styled.div`
  position: relative;
`;

const DatePickerInput = styled(DatePicker) <{ hasError: boolean, small?: boolean, xs?: boolean }>`
  ${InputCss};
  ${({
    theme, disabled, hasError, xs,
  }) => `
    background-color: ${xs ? theme.colours.neutral[9] : theme.colours.neutral[10]};
    font-weight: ${theme.fontWeight.normal};
    ${xs ? `${theme.padding[2]}` : ''};
    border-color: ${hasError ? theme.textDanger : theme.colours.neutral[6]};
    ${!disabled && `
      &:hover {
        border-color: ${hasError ? theme.textDanger : theme.colours.primary[5]}; 
      }
      &:focus {        
        box-shadow: 0px 0px 0px ${theme.padding[1]} ${hasError ? theme.danger : theme.colours.primary[11]};
        background-color: ${theme.colours.neutral[10]};
        border: 1px solid ${hasError ? theme.textDanger : theme.colours.primary[5]};
      }
    `};
    &:disabled {
      background-color: ${theme.colours.neutral[8]};
      border-color: ${theme.colours.neutral[8]};
    }
  `};
`;

type DateInputProps = {
  id: string,
  label?: string,
  value: string,
  errors?: string[],
  onChange: (date: Date | string) => void,
  labelStyles?: string,
  disabled?: boolean,
  touchOverride?: boolean,
  onBlur?: (targetValue: string) => void,
  placeholder?: string,
  required?: boolean,
  small?: boolean,
  xs?: boolean,
  inline?: boolean,
  portalId?: string,
  showIcon?: boolean,
  maxDate?: Date | null,
  isTooltipError?: boolean,
}

export function DateInput({
  id, label, value, errors = [], onChange, labelStyles = '', disabled = false, touchOverride = false, onBlur = () => { }, placeholder = '', required = false, small = false, inline = false, portalId, showIcon = true, xs = false, maxDate = null, isTooltipError = false,
}: DateInputProps) {
  const [touched, setTouched] = useState(false);
  const hasError = (touched || touchOverride) && !!errors.length;

  const datePickerRef = useRef<DatePicker>(null);

  const { isDisabled } = useFormConfigurationContext(disabled);

  const closeDatepicker = () => {
    datePickerRef?.current?.setOpen(false);
  };

  useEffect(() => {
    function watchScroll() {
      window.addEventListener('scroll', closeDatepicker);
    }
    watchScroll();
    return () => {
      window.removeEventListener('scroll', closeDatepicker);
    };
  });

  const onTouched = (targetValue: string) => {
    setTouched(true);
    onBlur(targetValue);
  };

  return (
    <Input id={id} label={label} errors={errors} hasError={hasError} labelStyles={labelStyles} required={required} inline={inline} isTooltipError={isTooltipError}>
      <DatePickerInputContainer>
        <DatePickerInput
          hasError={hasError}
          ref={datePickerRef}
          id={id}
          dateFormat="dd/MM/yyyy"
          selected={initDate(value).isValid() ? initDate(value).toDate() : null}
          onChange={(newValue: any, e) => {
            const inputValue = (e?.target as HTMLTextAreaElement).value;
            onChange(isValidDate(inputValue, DateFormatStringEnum.DDMMYYYY) ? newValue : inputValue);
            setTouched(true);
          }}
          onBlur={(e) => {
            if (!isValidDate(e.target.value, DateFormatStringEnum.DDMMYYYY)) {
              onChange('');
            }

            onTouched(e.target.value);
          }}
          onSelect={(e) => {
            onChange(e);
          }}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          scrollableYearDropdown
          maxDate={maxDate}
          disabled={isDisabled}
          placeholderText={placeholder}
          small={small}
          portalId={portalId}
          autoComplete="off"
          xs={xs}
        />
        {showIcon && <Icon className="mi-calendar" />}
      </DatePickerInputContainer>
    </Input>
  );
}
